import {
  filterGoalsByFilterOptions,
  getDeadlineGoals,
  getWeekStartsOn,
} from '@griply/utils';
import { useGoals } from 'features/goals';
import { useFilterDocumentFilters } from 'features/tasks';
import { useMemo } from 'react';
import { useFilterSlices } from 'shared/contexts/filter-slices';
import { useUpcomingCalendarViewOptions } from 'shared/contexts/upcoming-calendar-view-options';
import { useUser } from 'shared/hooks/use-user';
import { getGoalsDeadlineEvents } from 'shared/lib/full-calendar';
import { ID } from 'shared/types/id';
import { filterGoalsByHiddenLifeAreas } from 'shared/utils/filter-goals-by-hidden-life-areas';

import { useUpcomingFilterDocument } from './use-upcoming-filter-document';

export const useGoalDeadlineEvents = (hiddenGoalIds: Set<ID>) => {
  const goals = useGoals();
  const user = useUser();
  const weekStartsOn = getWeekStartsOn(user);
  const slices = useFilterSlices();
  const { viewOptions } = useUpcomingCalendarViewOptions();

  // The calendar supplies its own date window, so the `or` branches are dropped here too.
  const upcomingFilterDocument = useUpcomingFilterDocument();
  const filterDocument = useMemo(
    () => ({ ...upcomingFilterDocument, or: null }),
    [upcomingFilterDocument],
  );
  const filters = useFilterDocumentFilters(filterDocument, {});

  // Completed goals stay — the calendar dims them rather than hiding them.
  const filteredGoals = useMemo(
    () =>
      filterGoalsByFilterOptions(
        getDeadlineGoals(goals, { showCompleted: true }),
        filters,
      ),
    [goals, filters],
  );

  const visibleGoals = useMemo(
    () =>
      filterGoalsByHiddenLifeAreas(filteredGoals, {
        hiddenLifeAreaIds: viewOptions.hiddenLifeAreaIds,
        hiddenGoalIds,
        lifeAreaLinkedGoalIds: slices?.lifeAreaLinkedGoalIds,
      }),
    [
      filteredGoals,
      viewOptions.hiddenLifeAreaIds,
      hiddenGoalIds,
      slices?.lifeAreaLinkedGoalIds,
    ],
  );

  return useMemo(
    () =>
      viewOptions.hideGoalDeadlines
        ? []
        : getGoalsDeadlineEvents(visibleGoals, weekStartsOn),
    [viewOptions.hideGoalDeadlines, visibleGoals, weekStartsOn],
  );
};
